import { Link, useParams } from 'react-router-dom';
import type { RoleDetailResponse, RoleRequirementResponse, RoleRequirementsResponse } from '../api/types';
import { ApiError, describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import {
  REQUIREMENT_LEVEL_COPY,
  SUBJECT_TYPE_COPY,
  SUBJECT_TYPE_ORDER,
  describeThreshold,
} from '../components/role/roleDisplay';
import styles from './RoleDetailPage.module.css';

/**
 * One role: what it is, and what it requires
 * (docs/UX_ARCHITECTURE.md §2's "role detail with requirements").
 *
 * Two requests, two independent failures — the same reasoning as the
 * employee profile: a failing requirements request must not cost the reader
 * the role's own description.
 *
 * Requirements are grouped by the category the domain assigns them and
 * never flattened into one checklist. An attribute threshold on the 1-20
 * scale, a skill proficiency level, a credential that is held or not, and a
 * number of recorded years are four different kinds of claim, evaluated by
 * four different rules (domain/src/rules/requirementEvaluation.ts).
 * Presenting them as interchangeable ticks would erase exactly the
 * distinction docs/DOMAIN_MODEL.md keeps.
 *
 * No employee appears on this page. It is a definition, and
 * docs/ROLE_FRAMEWORK.md §5 keeps "who fits this role" out of a role view
 * at MVP — that question is asked from a person's own profile, about their
 * own current role.
 */
export function RoleDetailPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const role = useApiResource<RoleDetailResponse>(roleId ? `/roles/${roleId}` : null);
  const requirements = useApiResource<RoleRequirementsResponse>(roleId ? `/roles/${roleId}/requirements` : null);

  if (role.status === 'loading') return <LoadingState label="Loading role…" />;

  if (role.status === 'failed') {
    const notFound = role.error instanceof ApiError && role.error.status === 404;
    return (
      <div className={styles.page}>
        <BackLink />
        <ErrorState
          title={notFound ? 'This role could not be found' : 'This role could not be loaded'}
          detail={notFound ? 'No role in the library matches that address.' : describeError(role.error)}
        />
      </div>
    );
  }

  const detail = role.data;

  return (
    <div className={styles.page}>
      <BackLink />

      <header className={styles.header}>
        <h1 className={styles.name}>{detail.name}</h1>
        <dl className={styles.facts}>
          <div>
            <dt>Level</dt>
            <dd>{detail.levelLabel ?? <span className={styles.absent}>Not recorded</span>}</dd>
          </div>
          <div>
            <dt>Department</dt>
            <dd>{detail.departmentName ?? <span className={styles.absent}>Not recorded</span>}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{detail.status === 'retired' ? 'Retired' : 'Active'}</dd>
          </div>
        </dl>
      </header>

      {detail.responsibilities || detail.developmentExpectations ? (
        <section className={styles.prose} aria-label="About this role">
          {detail.responsibilities ? (
            <div>
              <h2 className={styles.proseHeading}>Responsibilities</h2>
              <p>{detail.responsibilities}</p>
            </div>
          ) : null}
          {detail.developmentExpectations ? (
            <div>
              <h2 className={styles.proseHeading}>Development expectations</h2>
              <p>{detail.developmentExpectations}</p>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className={styles.requirements} aria-labelledby="requirements-heading">
        <header className={styles.requirementsHeader}>
          <h2 id="requirements-heading" className={styles.requirementsHeading}>
            Requirements
          </h2>
          {requirements.status === 'loaded' ? (
            <p className={styles.summary}>
              {requirements.data.requirements.length}{' '}
              {requirements.data.requirements.length === 1 ? 'requirement' : 'requirements'} currently in effect
            </p>
          ) : null}
        </header>

        {requirements.status === 'loading' ? <LoadingState label="Loading requirements…" /> : null}
        {requirements.status === 'failed' ? (
          <ErrorState title="Requirements could not be loaded" detail={describeError(requirements.error)} />
        ) : null}

        {requirements.status === 'loaded' ? (
          requirements.data.requirements.length === 0 ? (
            <EmptyState
              title="No requirements are recorded"
              detail="Nothing has been defined for this role yet, so there is nothing for anyone to be compared against."
            />
          ) : (
            <RequirementGroups requirements={requirements.data.requirements} />
          )
        ) : null}
      </section>
    </div>
  );
}

/** Grouped by category, in the order the profile presents the same kinds of
 * information, with categories that have no requirements simply absent. */
function RequirementGroups({ requirements }: { requirements: RoleRequirementResponse[] }) {
  const byType = new Map<string, RoleRequirementResponse[]>();
  for (const requirement of requirements) {
    const bucket = byType.get(requirement.subjectType);
    if (bucket) bucket.push(requirement);
    else byType.set(requirement.subjectType, [requirement]);
  }

  return (
    <>
      {SUBJECT_TYPE_ORDER.filter((type) => byType.has(type)).map((type) => (
        <div key={type} className={styles.group}>
          <h3 className={styles.groupHeading}>{SUBJECT_TYPE_COPY[type].plural}</h3>
          <ul className={styles.list}>
            {byType.get(type)!.map((requirement) => (
              <li key={requirement.id} className={styles.requirement}>
                <span className={styles.subject}>{requirement.subjectName}</span>
                <span
                  className={
                    requirement.requirementLevel === 'required' ? styles.required : styles.preferred
                  }
                  title={REQUIREMENT_LEVEL_COPY[requirement.requirementLevel].meaning}
                >
                  {REQUIREMENT_LEVEL_COPY[requirement.requirementLevel].label}
                </span>
                <span className={styles.threshold}>{describeThreshold(requirement)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function BackLink() {
  return (
    <Link to="/roles" className={styles.back}>
      ← All roles
    </Link>
  );
}
